import { preloadEditorData } from '@confluence/load-edit-page/entry-points/preloadEditorData.ts';
import { prepareGuardExceptionTask } from '@confluence/query-preloader-tools/entry-points/prepareGuardExceptionTask.ts';
import { preloadEditorOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadEditorOnboardingState.ts';
import { preloadLandUsersInEditorWithRevampedBpqa } from '@confluence/onboarding-helpers/entry-points/preloadLandUsersInEditorWithRevampedBpqa.ts';
import { createDataFetcherForSuspense } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';
import { preloadNthUserEmojiTitleDiscovered } from '@confluence/onboarding-helpers/entry-points/preloadNthUserEmojiTitleDiscovered.ts';
import { preloadNthUserOnboardingCompletedTimestamp } from '@confluence/onboarding-helpers/entry-points/preloadNthUserOnboardingCompletedTimestamp.ts';
import { preloadEmojiEverSetProperty } from '@confluence/emoji-title/entry-points/preloadEmojiEverSetProperty.ts';
import { preloadSpaceDetail } from '@confluence/space-utils/entry-points/index.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { preloadDraft } from '@confluence/fabric-providers/entry-points/preloadDraft.ts';
import { preloadEditorByLineData } from '@confluence/content-header/entry-points/preloadEditorByLineData.ts';
import { preloadContentTypesHeader } from '@confluence/content-types-header/entry-points/preloadContentTypesHeader.js';
import { preloadClassificationContextForEditContent } from '@confluence/data-classification/entry-points/queries.ts';
import { preloadMediaToken } from '@confluence/fabric-media-support/entry-points/preloadMediaToken.ts';
import { preloadPageRestrictionsQuery } from '@confluence/page-restrictions-context/entry-points/preloadPageRestrictionsQuery.ts';
import { preloadRequestViewQuery } from '@confluence/no-permission/entry-points/preloadRequestViewQuery.ts';
import { preloadShareAndRestrictButtonQuery } from '@confluence/share-and-restrict-dialog/entry-points/preloadShareAndRestrictButtonQuery.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals, UNSAFE_expValNoExposure } from '@confluence/feature-experiments';
import { preloadSmartCreateFeedbackBanner } from '@confluence/editor-action-bar/entry-points/preloadSmartCreateFeedbackBanner.ts';
import {
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import {
	preloadWebPanelLocation,
	preloadWebPanelLocations,
} from '@confluence/web-panel-location/entry-points/preloadWebPanelLocation.ts';
import { preloadRecordLoomButton } from '@confluence/loom-utils/entry-points/preloadRecordLoomButton.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { FORGE_MODULE_XEN_MACRO } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { preloadUseExtensionList } from '@confluence/forge-ui/entry-points/preloadUseExtensionList.ts';
import { getIsEmbeddedConfluence } from '@confluence/query-preloaders-common/entry-points/matchHelpers.ts';
import { matchEditBlog } from '@confluence/query-preloaders-common/entry-points/matchRoutes.ts';
import { preloadNavigationTasks } from '@confluence/query-preloaders-common/entry-points/preloadNavigationTasks.ts';
import { preloadAudioContent } from '@confluence/audio/entry-points/preloadAudioContent.ts';
import { preloadApprovals } from '@confluence/approvals/entry-points/preloadApprovals.ts';
import { isSSRStreaming } from '@confluence/ssr-utilities/entry-points/isSSRStreaming.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';

import { preloadConnectedThirdPartyConfig } from './preloadConnectedThirdPartyConfig.ts';

export const preloadEditPageRoute = async (
	match: RouteMatch,
	_url: string,
	isTransition: boolean = false,
): Promise<void | any[] | Error[]> => {
	const {
		userId,
		isLicensed,
		cloudId,
		activationId,
		accessStatus,
		isLoggedIn,
		environment,
		edition,
	} = getPreloaderFnContext();
	const tasks: Promise<any>[] = [];
	const { contentId, spaceKey } = match.params;

	const isBlog = Boolean(matchEditBlog(_url));

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const shouldPreloadMentionsProvider = Boolean(process.env.REACT_SSR);
	const isEmbeddedConfluence = getIsEmbeddedConfluence(match);
	const shouldPreloadNavigation = !isEmbeddedConfluence;

	// During 'platform_editor_editor_ssr_streaming' remove this variable and use prepareGuardExceptionTask directly
	// in tasks.push
	const preloadPromise = prepareGuardExceptionTask('EditorData', () =>
		preloadEditorData({
			spaceKey,
			contentId,
			cloudId,
			shouldPreloadMentionsProvider,
			useCache: false,
			isBlog,
		}),
	);

	tasks.push(
		isSSRStreaming()
			? createDataFetcherForSuspense('content-main', () => preloadPromise)
			: preloadPromise,
	);

	// we dont know if a page is unpublished until we get data back. guess based on the draftShareId being in the url
	const guessIsUnpublishedDraft = Boolean(match?.query?.draftShareId);
	tasks.push(
		preloadEditorByLineData({
			isUnpublishedDraft: guessIsUnpublishedDraft,
			spaceKey,
			contentId,
		}),
	);

	if (isSSRStreaming()) {
		tasks.push(
			createDataFetcherForSuspense('content-types-header', () => {
				const contentTypesHeaderPreloadTasks: Promise<any>[] = [];

				contentTypesHeaderPreloadTasks.push(preloadContentTypesHeader(contentId));
				// Standalone Restrictions button needs to have its data preloaded
				contentTypesHeaderPreloadTasks.push(preloadPageRestrictionsQuery(contentId, false));

				if (fg('confluence_frontend_usd_ssr')) {
					contentTypesHeaderPreloadTasks.push(preloadShareAndRestrictButtonQuery(contentId));
				}

				return contentTypesHeaderPreloadTasks;
			}),
		);
	} else {
		tasks.push(preloadContentTypesHeader(contentId));
		// Standalone Restrictions button needs to have its data preloaded
		tasks.push(preloadPageRestrictionsQuery(contentId, false));

		// Unified Share and Restrict Button
		if (fg('confluence_frontend_usd_ssr')) {
			tasks.push(preloadShareAndRestrictButtonQuery(contentId));
		}
	}

	if (isLicensed) {
		tasks.push(preloadEditorOnboardingState());
		tasks.push(preloadLandUsersInEditorWithRevampedBpqa());
		// @TODO: check if we need to remove preloadNthUserOnboardingCompletedTimestamp, preloadNthUserEmojiTitleDiscovered, preloadEmojiEverSetProperty if auto_page_emoji_for_new_users_exp is dialed down.
		tasks.push(preloadNthUserOnboardingCompletedTimestamp());
		tasks.push(preloadNthUserEmojiTitleDiscovered());
		tasks.push(preloadEmojiEverSetProperty(contentId));
	}

	// Warm the third-party connector-config *code chunk* off the editor's critical
	// path so `useConnectedThirdPartyProductKeys` has its module ready at editor
	// mount. This intentionally does NOT issue the connector-config network request
	// — doing so here plants a stale-while-revalidate cache entry that the
	// editor-mount hook then serves stale from, wedging the quick-insert menu on a
	// partial app list. See preloadConnectedThirdPartyConfig for the full rationale.
	// Client-only, scoped to the `confluence_quick_insert_embeds` experiment cohort.
	// Read without exposure — the authoritative exposure fires inside the provider
	// builder at editor mount. Fire-and-forget: intentionally not pushed into `tasks`.
	if (
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		!process.env.REACT_SSR &&
		UNSAFE_expValNoExposure('confluence_quick_insert_embeds', 'cohort', 'control') !== 'control'
	) {
		preloadConnectedThirdPartyConfig();
	}

	if (isTransition) {
		if (expValEquals('cc_editor_edit_page_preload_loadables', 'isEnabled', true)) {
			void import(
				/* webpackChunkName: "loadable-editor-loadables-preloader" */
				'@confluence/editor-loadables-preloader/entry-points/preloadEditPageLoadables.ts'
			)
				.then(({ preloadEditPageLoadables }) => preloadEditPageLoadables())
				.catch(() => {});
		}

		if (expValEquals('web_panel_query_batch_call', 'isEnabled', true)) {
			tasks.push(
				preloadWebPanelLocations({
					contentId,
					locations: [ATL_GENERAL, ATL_FOOTER],
				}),
			);
		}

		return waitForAllReturnWhenError(tasks);
	}

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		if (isSSRStreaming()) {
			if (shouldPreloadNavigation) {
				tasks.push(
					createDataFetcherForSuspense('side-navigation', () => {
						// Navigation and page tree
						return preloadNavigationTasks(spaceKey, contentId, isLicensed, isBlog);
					}),
				);
			}
		} else {
			// Navigation and page tree
			if (shouldPreloadNavigation) {
				tasks.push(preloadNavigationTasks(spaceKey, contentId, isLicensed, isBlog));
			}
		}

		tasks.push(preloadMediaToken(contentId), preloadClassificationContextForEditContent(contentId));
	} else {
		tasks.push(preloadDraft(contentId));
	}

	tasks.push(preloadSpaceDetail(spaceKey));

	if (expValEquals('web_panel_query_batch_call', 'isEnabled', true)) {
		tasks.push(
			preloadWebPanelLocations({
				contentId,
				locations: [ATL_GENERAL, ATL_FOOTER],
			}),
		);
	} else {
		tasks.push(
			preloadWebPanelLocation({ contentId, location: ATL_GENERAL }),
			preloadWebPanelLocation({ contentId, location: ATL_FOOTER }),
		);
	}

	tasks.push(preloadRequestViewQuery(contentId));

	if (
		edition === ConfluenceEdition.PREMIUM &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true)
	) {
		tasks.push(preloadApprovals({ cloudId, contentId, contentStatus: 'DRAFT', spaceKey }));
	}

	// Preload Loom button in Action Bar
	if (
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS) &&
		isLoggedIn
	) {
		tasks.push(
			preloadRecordLoomButton({
				cloudId,
				activationId,
				accessStatus,
				isLoggedIn,
				userId,
				environment,
			}),
		);
	}

	// Preload forge extension manifest
	if (fg('confluence_preload_forge_viewport_heights_editor')) {
		tasks.push(
			preloadUseExtensionList({
				contentId,
				spaceKey,
				moduleType: FORGE_MODULE_XEN_MACRO,
			}),
		);
	}

	// Preload Smart Create Feedback Banner
	if (fg('aifc_create_enabled')) {
		tasks.push(preloadSmartCreateFeedbackBanner(contentId));
	}

	// Preload Audio Listen Button
	tasks.push(preloadAudioContent(contentId));

	return waitForAllReturnWhenError(tasks);
};
